import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, CheckCircle2, Heart, Users, TrendingUp, Shield, Brain, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <main className="pt-24 bg-[#FDFDFB]">
      <SEO 
        title="Home - Compassionate Mental Health Care"
        description="Compassionate, modern mental health care grounded in science and whole-person understanding. Now accepting new clients in GA, TN, MD, and DC."
        keywords="psychiatric care, mental health treatment, psychiatry Georgia, anxiety treatment, depression treatment, medication management"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#f7f4f0] to-[#FDFDFB] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-[#3a3a3a] mb-6">
              Harmony Behavioral & Psychiatric Health
            </h1>
            <p className="text-2xl text-[#7a8a72] mb-8 leading-relaxed">
              Compassionate, modern mental health care grounded in science and whole-person understanding.
            </p>
            <Link to="/book-appointment">
              <Button size="lg" className="bg-[#c9b17a] hover:bg-[#b89d66] text-white text-xl px-12 py-6 rounded-lg shadow-lg">
                Now Accepting New Clients — Book Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Your Care Journey Section */}
      <section className="py-16 bg-[#FDFDFB]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-[#7a8a72] text-center mb-12">Your Care Journey</h2>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Card className="flex-1 p-6 text-center bg-[#a8b5a0] border-[#d4cec5] hover:shadow-lg transition-shadow">
                <div className="text-[#7a8a72] mb-2">
                  <Users className="mx-auto" size={40} />
                </div>
                <h3 className="font-semibold text-[#3a3a3a]">Connect</h3>
              </Card>
              <ArrowRight className="text-[#7a8a72] hidden md:block" size={24} />
              <Card className="flex-1 p-6 text-center bg-[#a8b5a0] border-[#d4cec5] hover:shadow-lg transition-shadow">
                <div className="text-[#7a8a72] mb-2">
                  <Brain className="mx-auto" size={40} />
                </div>
                <h3 className="font-semibold text-[#3a3a3a]">Comprehensive Assessment</h3>
              </Card>
              <ArrowRight className="text-[#7a8a72] hidden md:block" size={24} />
              <Card className="flex-1 p-6 text-center bg-[#a8b5a0] border-[#d4cec5] hover:shadow-lg transition-shadow">
                <div className="text-[#7a8a72] mb-2">
                  <Heart className="mx-auto" size={40} />
                </div>
                <h3 className="font-semibold text-[#3a3a3a]">Personalized Treatment Plan</h3>
              </Card>
              <ArrowRight className="text-[#7a8a72] hidden md:block" size={24} />
              <Card className="flex-1 p-6 text-center bg-[#a8b5a0] border-[#d4cec5] hover:shadow-lg transition-shadow">
                <div className="text-[#7a8a72] mb-2">
                  <Shield className="mx-auto" size={40} />
                </div>
                <h3 className="font-semibold text-[#3a3a3a]">Ongoing Medication Management</h3>
              </Card>
              <ArrowRight className="text-[#7a8a72] hidden md:block" size={24} />
              <Card className="flex-1 p-6 text-center bg-[#a8b5a0] border-[#d4cec5] hover:shadow-lg transition-shadow">
                <div className="text-[#7a8a72] mb-2">
                  <TrendingUp className="mx-auto" size={40} />
                </div>
                <h3 className="font-semibold text-[#3a3a3a]">Long-Term Stability</h3>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Feel Like Yourself Again Section */}
      <section className="py-16 bg-[#f7f4f0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Sparkles className="mx-auto text-[#7a8a72] mb-6" size={48} />
            <h2 className="text-4xl font-bold text-[#7a8a72] mb-6">Feel Like Yourself Again</h2>
            <p className="text-xl text-[#3a3a3a] leading-relaxed">
              Our care is designed to help you regain emotional balance, clarity, stability, and a sense of control over your life — not just manage symptoms.
            </p>
          </div>
        </div>
      </section>

      {/* Why Patients Choose Us Section */}
      <section className="py-16 bg-[#FDFDFB]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-[#7a8a72] text-center mb-12">Why Patients Choose Us</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6 bg-white border-[#d4cec5] hover:shadow-lg transition-shadow">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-xl font-semibold text-[#3a3a3a] mb-2">Evidence-based psychiatric care</h3>
              </Card>
              <Card className="p-6 bg-white border-[#d4cec5] hover:shadow-lg transition-shadow">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-xl font-semibold text-[#3a3a3a] mb-2">Personalized treatment plans</h3>
              </Card>
              <Card className="p-6 bg-white border-[#d4cec5] hover:shadow-lg transition-shadow">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-xl font-semibold text-[#3a3a3a] mb-2">Integrated medication management</h3>
              </Card>
              <Card className="p-6 bg-white border-[#d4cec5] hover:shadow-lg transition-shadow">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-xl font-semibold text-[#3a3a3a] mb-2">Respectful, non-stigmatizing approach</h3>
              </Card>
              <Card className="p-6 bg-white border-[#d4cec5] hover:shadow-lg transition-shadow">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-xl font-semibold text-[#3a3a3a] mb-2">Whole-person perspective</h3>
              </Card>
              <Card className="p-6 bg-white border-[#d4cec5] hover:shadow-lg transition-shadow">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-xl font-semibold text-[#3a3a3a] mb-2">Focus on long-term stability, not quick fixes</h3>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Provider Section */}
      <section className="py-16 bg-[#f7f4f0]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Card className="overflow-hidden hover-lift bg-white border-[#d4cec5]">
              <div className="md:flex items-center gap-8 p-8 md:p-12">
                <div className="flex-shrink-0 mb-6 md:mb-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_harmony-behavioral/artifacts/0xv7f47s_starks_shaquita_default_circle_crop_-_street_headshot.jpeg"
                    alt="Ms. Shaquita Starks"
                    className="w-48 h-48 rounded-full object-cover mx-auto shadow-lg"
                  />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-[#7a8a72] mb-2">
                    Shaquita Starks, PhD, PMHNP-BC, FAAN
                  </h2>
                  <p className="text-lg text-[#7a8a72] font-semibold mb-4">
                    Board-Certified Psychiatric Mental Health Nurse Practitioner
                  </p>
                  <p className="text-[#3a3a3a] leading-relaxed">
                    Dedicated to providing high-quality, evidence-informed care Ms. Starks specializes in
                    comprehensive psychiatric health for patients across the life-span (ages 5 and up) across
                    Tennessee, Georgia, Maryland, and the District of Columbia. With an extensive clinical
                    background and a commitment to clinical excellence, Ms. Starks focuses on personalized
                    treatment plans that empower individuals to achieve mental wellness and functional balance.
                    By integrating the latest clinical research with a patient-centered approach, the practice
                    offers a supportive environment for managing complex behavioral health needs.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#7a8a72]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Begin Your Journey?</h2>
            <p className="text-xl text-white/90 mb-8">
              Take the first step toward emotional balance and stability
            </p>
            <Link to="/book-appointment">
              <Button size="lg" className="bg-[#c9b17a] hover:bg-[#b89d66] text-white text-xl px-12 py-6 rounded-lg shadow-lg">
                Now Accepting New Clients — Book Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;